import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';
import HomeSizzleArea from './HomeSizzleArea';
import HomeContent from './HomeContent';
import HomeVideo from './HomeVideo';
import PageSEO from './PageSEO'; 

const Home = () => {

  const [randomLocationImage, setRandomLocationImage] = useState('');

  const copyWithLinks = (
    <>
      Our butcher and deli shops are the heart of Kinder's East Bay legacy. Kinder's remains committed to quality, from our cut-to-order steaks and marinated meats by the pound to our made-to-order deli and smoked BBQ specialties. Visit one of our six locations in{' '}
      <Link to="/locations/brentwood" className='colorBlack'>Brentwood</Link>,{' '}
      <Link to="/locations/concord" className='colorBlack'>Concord</Link>,{' '}
      <Link to="/locations/hercules" className='colorBlack'>Hercules</Link>,{' '}
      <Link to="/locations/martinez" className='colorBlack'>Martinez</Link>,{' '}
      <Link to="/locations/pleasant-hill" className='colorBlack'>Pleasant Hill</Link>,{' and '}
      <Link to="/locations/walnut-creek-ygnacio" className='colorBlack'>Walnut Creek</Link>{' '}
      to taste the difference.
    </>
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    // Array of location images
    const locationImages = [
      '/images/homepage-locations-content-2.webp',
      '/images/homepage-locations-content-3.webp',
      '/images/homepage-locations-content-4.webp',
      '/images/homepage-locations-content-5.webp'
    ];
    
    // Generate random index (0-3)
    const randomIndex = Math.floor(Math.random() * locationImages.length);
    
    // Set the random image
    setRandomLocationImage(locationImages[randomIndex]);
  }, []);

   // Comprehensive SEO configuration for homepage
  const homepageSEO = {
    title: "Kinder's Meats Deli BBQ & Catering | Family-Owned California BBQ Since 1946",
    description: "Family-owned BBQ restaurant serving authentic barbecue, premium deli meats, and professional catering across 7 California locations. Three generations of quality, community, and consistently delicious food since 1946.",
    keywords: [
      "Kinder's Meats",
      "BBQ restaurant California",
      "family-owned BBQ", 
      "East Bay BBQ",
      "barbecue catering",
      "deli meats",
      "cut-to-order steaks",
      "smoked BBQ specialties",
      "California barbecue",
      "BBQ Brentwood",
      "BBQ Concord", 
      "BBQ Hercules",
      "BBQ Martinez",
      "BBQ Pleasant Hill",
      "BBQ Walnut Creek",
      "authentic barbecue",
      "community restaurant",
      "quality meats",
      "family restaurant California"
    ],
    canonicalUrl: "/",
    ogImage: "https://kindersmeats.com/images/kinders-homepage-og-image.jpg",
    customMeta: {
      'geo.region': 'CA-US',
      'geo.placename': 'California, East Bay',
      'business.contact_data.country_name': 'United States',
      'business.contact_data.region': 'California'
    },
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": "https://kindersmeats.com/#organization",
          "name": "Kinder's Meats Deli BBQ & Catering",
          "alternateName": "Kinder's Meats",
          "url": "https://kindersmeats.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://kindersmeats.com/images/kinders-logo.png",
            "width": 300,
            "height": 100,
            "caption": "Kinder's Meats Deli BBQ & Catering Logo"
          },
          "image": {
            "@type": "ImageObject",
            "url": "https://kindersmeats.com/images/kinders-three-generations.jpg",
            "width": 800,
            "height": 600,
            "caption": "Three Generations of the Kinder Family"
          },
          "description": "Family-owned BBQ restaurant chain serving authentic barbecue, premium deli meats, and professional catering services across California since 1946. Three generations committed to quality, community, and consistently delicious food.",
          "foundingDate": "1946",
          "slogan": "Family-owned. Community-loved. Consistently delicious.",
          "keywords": "BBQ restaurant, barbecue catering, deli meats, family-owned restaurant, California BBQ",
          "knowsAbout": [
            "Barbecue",
            "Deli meats", 
            "Catering services",
            "Cut-to-order steaks",
            "Smoked meats",
            "Food service"
          ],
          "memberOf": {
            "@type": "Organization",
            "name": "California Restaurant Association"
          },
          "award": "East Bay's Favorite BBQ Restaurant",
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "CA",
            "addressCountry": "US",
            "addressLocality": "East Bay California"
          },
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "availableLanguage": ["English"],
              "areaServed": "California"
            },
            {
              "@type": "ContactPoint",
              "contactType": "catering",
              "availableLanguage": ["English"], 
              "areaServed": "California"
            }
          ],
          "sameAs": [
            "https://www.facebook.com/KindersMeats",
            "https://www.instagram.com/kindersmeats",
            "https://www.yelp.com/biz/kinders-meats"
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Kinder's Menu & Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "MenuItem",
                  "name": "BBQ & Smoked Meats",
                  "description": "Authentic slow-smoked BBQ specialties"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "MenuItem",
                  "name": "Premium Deli Meats",
                  "description": "Cut-to-order premium deli meats and steaks"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "BBQ Catering Services",
                  "description": "Professional catering for events and gatherings"
                }
              }
            ]
          },
          "location": [
            {
              "@type": "Place",
              "name": "Brentwood Location",
              "address": { "@type": "PostalAddress", "addressLocality": "Brentwood", "addressRegion": "CA" }
            },
            {
              "@type": "Place", 
              "name": "Concord Location",
              "address": { "@type": "PostalAddress", "addressLocality": "Concord", "addressRegion": "CA" }
            },
            {
              "@type": "Place",
              "name": "Hercules Location", 
              "address": { "@type": "PostalAddress", "addressLocality": "Hercules", "addressRegion": "CA" }
            },
            {
              "@type": "Place",
              "name": "Martinez Location",
              "address": { "@type": "PostalAddress", "addressLocality": "Martinez", "addressRegion": "CA" }
            },
            {
              "@type": "Place",
              "name": "Pleasant Hill Location",
              "address": { "@type": "PostalAddress", "addressLocality": "Pleasant Hill", "addressRegion": "CA" }
            },
            {
              "@type": "Place",
              "name": "Walnut Creek Location",
              "address": { "@type": "PostalAddress", "addressLocality": "Walnut Creek", "addressRegion": "CA" }
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://kindersmeats.com/#website",
          "url": "https://kindersmeats.com",
          "name": "Kinder's Meats Deli BBQ & Catering",
          "description": "Official website for Kinder's Meats BBQ restaurants and catering services",
          "publisher": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "potentialAction": [
            {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://kindersmeats.com/locations?search={search_term_string}"
              },
              "query-input": "required name=search_term_string"
            }
          ],
          "mainEntity": {
            "@id": "https://kindersmeats.com/#organization"
          }
        },
        {
          "@type": "WebPage",
          "@id": "https://kindersmeats.com/#webpage", 
          "url": "https://kindersmeats.com",
          "name": "Kinder's Meats Deli BBQ & Catering | Family-Owned California BBQ Since 1946",
          "isPartOf": {
            "@id": "https://kindersmeats.com/#website"
          },
          "about": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "description": "Family-owned BBQ restaurant serving authentic barbecue, premium deli meats, and professional catering across 7 California locations since 1946.",
          "breadcrumb": {
            "@id": "https://kindersmeats.com/#breadcrumb"
          },
          "mainEntity": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "significantLink": [
            "https://kindersmeats.com/locations",
            "https://kindersmeats.com/catering", 
            "https://kindersmeats.com/menu"
          ]
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://kindersmeats.com/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://kindersmeats.com"
            }
          ]
        }
      ]
    }
  };
  
  return (
    <>
    <PageSEO {...homepageSEO} />
    <main>
    <section className="theme-dark section m-0 p-0" data-theme="dark">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>

            <HomeSizzleArea 
              colorMode="colorBlack"
              altColorMode="colorWhite"
              imageUrl="/images/kinders-bckgrd-d1.webp"
              headingOne="Lunch is going to be great."
              headingTwo="No One Leaves Hungry."
              headingThree="Family-owned. Community-loved. Consistently delicious."
            /> 

            {/* Spacer between blocks */}
            <div className="py-6"></div>

            <HomeContent 
              imageUrl="/images/kinders-three-generations.webp"
              imageAlt="Three Generations of Dedication to Quality and Service"
              headline="Three Generations. One Commitment."
              copyText="Since 1946, the Kinder family has been committed to creating a warm, welcoming experience built on quality, comfort, and community. From our early days as a neighborhood butcher shop to becoming a local favorite, one thing has stayed the same: we treat every customer like family and take pride in doing things right, every time."
              reversed={true}
              colorMode="colorWhite"
              buttonLabel="Learn More"
              buttonUrl="/our-story"
            />

            {/* Spacer between blocks */}
            <div className="py-6"></div>

            {/* copyText="Our butcher and deli shops are the heart of Kinder's East Bay legacy. Kinder's remains obsessed with quality: from our cut-to-order, pasture-raised steaks and marinated meats by the pound to our made-to-order deli, slow-smoked BBQ specialties, and crisp seasonal vegetables sourced from farms committed to sustainable practices. Visit one of our six locations in Brentwood, Concord, Hercules, Martinez, Pleasant Hill, and Walnut Creek to taste the difference." */}
            <HomeContent 
              imageUrl={randomLocationImage || '/images/homepage-locations-content-3.jpg'} 
              imageAlt="Always Nearby With Six East Bay Locations"
              headline="Always Nearby"
              copyText={copyWithLinks}
              reversed={false}
              colorMode="colorWhite"
              buttonLabel="Learn More"
              buttonUrl="/locations"
            />

            {/* Spacer between blocks */}
            <div className="py-6"></div>

            <HomeVideo 
              imageUrl="/images/home-sizzle-optimized.mp4"
              imageUrlMobile="/images/home-sizzle-mobile.mp4"
              colorMode="colorWhite"
            />

            {/* Spacer between blocks */}
            <div className="py-6"></div>

            {/* Second Copy Block - Image Right, Text Left */}
            <HomeContent 
              imageUrl="/images/kinders-proudly-serving.webp"
              imageAlt="Proudly Serving Our Community and Those Who Serve."
              headline="Proudly Serving Our Community and Those Who Serve."
              copyText="Kinder's is committed to supporting our community, especially the veterans and active-duty service members who give so much. We proudly use high-quality meats and locally sourced ingredients made in the USA. Veterans and active-duty military receive 15% off as our way of saying thanks."
              reversed={true}
              colorMode="colorWhite"
              buttonLabel="Learn More"
              buttonUrl="/catering"
            />
            
            <Navigation 
              colorMode="colorBlack"
            /> 
          
            <Footer 
              colorMode="colorBlack"
            />
        </div>
      </section>
      </main>
    </>
  );
};

export default Home;